import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { CirclePlus } from 'lucide-react';

import { Form } from '@/components/ui/form';
import Select from '@/components/form/Select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';

import { useTaxes } from '@/data/tax';
import { usePutRegion, useRegion } from '@/data/region';

import { z } from 'zod';

const formSchema = z.object({
  tax: z.string().min(1, { message: 'Required' })
});

const AddRegionTaxDialog = ({ regionId, regionTaxes }) => {
  const [open, setIsOpen] = useState(false);
  const region = useRegion(regionId);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const putRegion = usePutRegion();
  const taxes = useTaxes();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tax: ''
    }
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      setApiError('');
      await putRegion(regionId, {
        taxes: [...new Set([...regionTaxes.map((rt) => rt.tax.id), values.tax])]
      });
      region.mutate();
      setSubmitting(false);
      setIsOpen(false);
      form.reset();
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <CirclePlus className="mr-2 h-4 w-4" />
          Add Tax
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Tax</DialogTitle>
          <DialogDescription>Add a new tax rate</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Select
              name="tax"
              label="Tax"
              options={taxes.data.map((tax) => ({
                key: tax.id,
                value: tax.id,
                label: tax.name
              }))}
            />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>Add Tax</SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRegionTaxDialog;
